/** Loading and acting on the portal API from a screen. A 401 anywhere ends the session (sign in again). */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApiError, isAbortError, withReadSignal, type PortalApi } from "./client.ts";
import { markPortalRouteReady } from "../telemetry.ts";
import type { PracticeSession } from "../practice/practiceStore.ts";

export interface PortalContextValue {
  api: PortalApi;
  roles: readonly string[];
  email: string;
  /** The signed-in user's display name (/me). */
  name?: string;
  /** Outreach mode from /me (build unless the server says otherwise, AIC-112). */
  buildMode?: boolean;
  /** Called on any 401: the app shows the sign-in-again screen. */
  expire(): void;
  /** AIC-254: an admin's read-only analyst preview (/me has preview); screens skip calls the preview refuses. */
  preview?: boolean;
  /** Identity of a read-only preview; never shares personal browser preferences with an own workspace. */
  previewScope?: string;
  /** AIC-463: the active practice session; requests to the practice allowlist carry its id. null/absent: real work. */
  practice?: PracticeSession | null;
}

/** The server's refusal of a route outside the analyst preview's read allowlist. */
export const isPreviewRefusal = (e: unknown): boolean => e instanceof ApiError && e.code === "preview_read_only";
/** Stands in for a call the preview would refuse, without sending it (and without a denied audit row). */
export const previewRefusal = (): Promise<never> => Promise.reject(new ApiError(403, "preview_read_only"));

export const PortalContext = createContext<PortalContextValue | null>(null);

export function usePortal(): PortalContextValue {
  const v = useContext(PortalContext);
  if (!v) throw new Error("usePortal outside PortalContext");
  return v;
}

/** Marks a navigation ready only after this screen has committed every required successful load. */
export function useRouteReady(ready: boolean, routeKey?: string): void {
  useEffect(() => { if (ready) markPortalRouteReady(); }, [ready, routeKey]);
}

export type Load<T> =
  | { state: "loading" }
  | { state: "error"; error: ApiError | Error }
  | { state: "ready"; data: T };

/**
 * Runs fetcher(api) on mount and whenever deps change. reload() runs it again but keeps showing the current data
 * until the new data arrives, so a screen does not unmount (and lose, say, a link shown once) while refreshing.
 */
export function useLoad<T>(fetcher: (api: PortalApi) => Promise<T>, deps: readonly unknown[]): Load<T> & { reload: () => void } {
  const { api, expire } = usePortal();
  const [state, setState] = useState<Load<T>>({ state: "loading" });
  const [tick, setTick] = useState(0);
  const f = useRef(fetcher);
  f.current = fetcher;
  const lastDeps = useRef<readonly unknown[] | null>(null);
  useEffect(() => {
    let live = true;
    const controller = new AbortController();
    const sameDeps = lastDeps.current !== null && lastDeps.current.length === deps.length && lastDeps.current.every((d, i) => Object.is(d, deps[i]));
    lastDeps.current = deps;
    setState((prev) => (sameDeps && prev.state === "ready" ? prev : { state: "loading" }));
    f.current(withReadSignal(api, controller.signal)).then(
      (data) => { if (live) setState({ state: "ready", data }); },
      (error: unknown) => {
        if (!live || isAbortError(error)) return;
        if (error instanceof ApiError && error.status === 401) expire();
        setState({ state: "error", error: error instanceof Error ? error : new Error(String(error)) });
      },
    );
    return () => { live = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, expire, tick, ...deps]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/** A page of a server-paged list (AIC-268): the next page's cursor, or the page size to ask for. */
export interface PageRequest { cursor?: string; limit?: number }
/** The server's page size unless asked (tracker-api routes/paging.ts), and the most one request may ask for. */
export const PAGE_SIZE = 100;
export const PAGE_MAX = 500;

export type PagedLoad<T> = Load<T> & {
  reload: () => void;
  /** Appends the next page; resolves true when rows were added. */
  more: () => Promise<boolean>;
  loadingMore: boolean;
  moreError: ApiError | Error | null;
  /** Goes up each time a first page (a fresh load or a reload) arrives, not when a page is appended. */
  generation: number;
};

/**
 * A server-paged list (AIC-268). The first page loads on mount and whenever deps change, as useLoad; more() fetches
 * the page after the last one shown and merges it in (`merge`). reload() keeps showing the current rows and reads
 * as many as are shown (`size`, up to PAGE_MAX) in one request, so a refresh after a decision doesn't drop the pages
 * the user had opened. A response that arrives after the deps changed or a reload started is dropped.
 */
export function usePagedLoad<T extends { next_cursor?: string | null }>(
  fetcher: (api: PortalApi, page: PageRequest) => Promise<T>,
  merge: (shown: T, next: T) => T,
  size: (data: T) => number,
  deps: readonly unknown[],
): PagedLoad<T> {
  const { api, expire } = usePortal();
  const [state, setState] = useState<Load<T>>({ state: "loading" });
  const [tick, setTick] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<ApiError | Error | null>(null);
  const [generation, setGeneration] = useState(0);
  const f = useRef(fetcher);
  f.current = fetcher;
  const m = useRef(merge);
  m.current = merge;
  const shown = useRef<T | null>(null);
  shown.current = state.state === "ready" ? state.data : null;
  const gen = useRef(0);
  const lifetime = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const reloadSize = useRef<number | undefined>(undefined);
  const lastDeps = useRef<readonly unknown[] | null>(null);
  useEffect(() => {
    const my = ++gen.current;
    const controller = new AbortController();
    lifetime.current = controller;
    busy.current = false;
    const sameDeps = lastDeps.current !== null && lastDeps.current.length === deps.length && lastDeps.current.every((d, i) => Object.is(d, deps[i]));
    lastDeps.current = deps;
    const limit = sameDeps ? reloadSize.current : undefined;
    reloadSize.current = undefined;
    setState((prev) => (sameDeps && prev.state === "ready" ? prev : { state: "loading" }));
    setLoadingMore(false);
    setMoreError(null);
    f.current(withReadSignal(api, controller.signal), limit ? { limit } : {}).then(
      (data) => { if (gen.current === my) { setState({ state: "ready", data }); setGeneration((g) => g + 1); } },
      (error: unknown) => {
        if (gen.current !== my || isAbortError(error)) return;
        if (error instanceof ApiError && error.status === 401) expire();
        setState({ state: "error", error: error instanceof Error ? error : new Error(String(error)) });
      },
    );
    return () => { gen.current++; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, expire, tick, ...deps]);
  const reload = useCallback(() => {
    const n = shown.current ? size(shown.current) : 0;
    reloadSize.current = n > PAGE_SIZE ? Math.min(PAGE_MAX, n) : undefined;
    setTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const more = useCallback(async () => {
    const cursor = shown.current?.next_cursor;
    if (!cursor || busy.current || !lifetime.current || lifetime.current.signal.aborted) return false;
    const my = gen.current;
    busy.current = true;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const next = await f.current(withReadSignal(api, lifetime.current.signal), { cursor });
      if (gen.current !== my) return false;
      setState((s) => (s.state === "ready" ? { state: "ready", data: m.current(s.data, next) } : s));
      return true;
    } catch (error) {
      if (gen.current !== my || isAbortError(error)) return false;
      if (error instanceof ApiError && error.status === 401) expire();
      setMoreError(error instanceof Error ? error : new Error(String(error)));
      return false;
    } finally {
      if (gen.current === my) { busy.current = false; setLoadingMore(false); }
    }
  }, [api, expire]);
  return { ...state, reload, more, loadingMore, moreError, generation };
}

/** A search box's text as the server should get it: trimmed, once typing pauses; cleared at once (AIC-268). */
export function useSettledText(text: string, ms = 300): string {
  const trimmed = text.trim();
  const [settled, setSettled] = useState(trimmed);
  useEffect(() => {
    if (trimmed === settled) return;
    const t = setTimeout(() => setSettled(trimmed), trimmed ? ms : 0);
    return () => clearTimeout(t);
  }, [trimmed, settled, ms]);
  return settled;
}

/** A button's action: busy while running, the error kept for display, 401 → sign in again. */
export function useAction() {
  const { api, expire } = usePortal();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const run = useCallback(async <T,>(fn: (api: PortalApi) => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn(api);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) expire();
      setError(e instanceof Error ? e : new Error(String(e)));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, [api, expire]);
  return { busy, error, run, clearError: () => setError(null) };
}

/** What each server error code (tracker-api `error`) says to a person (AIC-557); a code this doesn't know says nothing of itself. */
export const ERROR_SENTENCE: Readonly<Record<string, string>> = {
  bad_request: "That request wasn't valid. Check what you entered and try again.",
  invalid_request: "That request wasn't valid. Check what you entered and try again.",
  invalid: "That request wasn't valid. Check what you entered and try again.",
  invalid_batch: "Some of the items in that request weren't valid. Check them and try again.",
  bad_cursor: "The list changed while you were paging through it. Reload it and try again.",
  unknown_field: "That field isn't part of this survey.",
  unsupported_media_type: "That file type isn't supported.",
  conflict: "Someone else changed this at the same time. Reload and try again.",
  report_changed: "That report was updated while you were working. Reload it and try again.",
  queue_changed: "That queue was updated while you were working. Reload it and try again.",
  portfolio_changed: "That list was updated while you were working. Reload it and try again.",
  wrong_status: "That can't be done at this step.",
  bad_transition: "That can't be done at this step.",
  not_open: "That item has already been closed.",
  not_pending: "That item has already been decided.",
  not_ready: "That isn't ready yet.",
  not_proposed: "That hasn't been proposed.",
  not_partial: "That interview wasn't left part-way.",
  not_left: "That company hasn't left the list.",
  not_confirmed: "That analyst hasn't been confirmed yet.",
  not_clearable: "That can't be cleared.",
  not_a_setting: "That isn't a setting.",
  no_such_warning: "That warning no longer exists.",
  no_snapshot: "There is no IDC snapshot for this company yet.",
  no_draft: "There is no draft to use.",
  no_code: "There is no code to show.",
  cycle_closed: "This cycle is closed.",
  call_live: "A call is in progress right now.",
  export_busy: "An export is already running for this result.",
  too_soon: "That was done too recently. Wait a moment and try again.",
  too_many: "Too many requests. Wait a moment and try again.",
  rate_limited: "Too many requests. Wait a moment and try again.",
  daily_limit: "The daily limit has been reached.",
  mint_limit: "The limit for new links has been reached.",
  live_mode: "That isn't available in live mode.",
  outside_window: "That is outside the allowed window.",
  contact_exists: "That contact already exists.",
  exists: "That already exists.",
  upn_taken: "That sign-in is already used by another analyst.",
  staff: "That is an IDC staff member, not a Data Layer owner.",
  covering: "Someone else is already covering that.",
  gone: "That is no longer available.",
  locked: "That is locked right now.",
  follow_up_open: "A follow-up is still open.",
  spec_has_interview: "That spec already has an interview.",
  sensitive_escalation_open: "A confidentiality request is still open on this result.",
  reactivate_required: "Reactivate it first.",
  domain_mismatch: "That email address is outside the company's domain.",
  graph_unavailable: "Outlook isn't available right now. Try again in a few minutes.",
  artifacts_unavailable: "The call recording isn't available.",
  assignment_not_found: "That assignment isn't available to you.",
  commit_failed: "The change couldn't be saved. Nothing was changed.",
  preview_read_only: "Read-only in analyst preview. Exit preview to act on this.",
  practice_read_only: "That can't be changed in practice.",
  practice_forbidden: "That practice session isn't yours.",
  practice_not_found: "That practice session isn't available.",
};
/** A server message worth showing: words, not an identifier, a validation path or JSON. */
const isReadable = (m: string | null): m is string => !!m && m.length <= 300 && !/\b[a-z0-9]+(?:_[a-z0-9]+)+\b/.test(m) && !/[{}[\]]/.test(m) && !/^[\w.]+:\s/.test(m) && !/\bInvalid input\b|\bexpected\b.*\breceived\b/i.test(m);
const sentenceOf = (e: ApiError): string | null => (isReadable(e.detail) ? e.detail : e.code ? ERROR_SENTENCE[e.code] ?? null : null);

/**
 * Plain-language message for an API failure: the server's message when it is a sentence, else the code's sentence, else a
 * generic line. Never an error code or a validation path.
 */
export function describeError(e: ApiError | Error): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return "You don't have access to this. It belongs to another analyst, or needs the admin role.";
    if (e.status === 404) return sentenceOf(e) ?? "Not found. It may have been removed, or the link is wrong.";
    // An older server sent a sentence as the code ("result is approved").
    if (e.status === 409) return sentenceOf(e) ?? (isReadable(e.code) && e.code.includes(" ") ? `That can't be done now: ${e.code}.` : "That can't be done in its current state.");
    if (e.status === 401) return "Your session has ended. Sign in again.";
    // Never the machine code ("unavailable", "snowflake_unavailable"): a person reads this.
    if (e.status === 503) return sentenceOf(e) ?? "This isn't available right now. Nothing has changed; try again in a few minutes.";
    return sentenceOf(e) ?? `Something went wrong (${e.status}). Try again.`;
  }
  return "Something went wrong. Try again.";
}
